import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { berserkr, getSupabase, isSupabaseConfigured } from "../lib/supabase";

const BUILD = "contradiction/0.14-2026-06-18";

export interface ContradictionStatus {
  total: number; assessed: number; contradicted: number; remaining: number; last_built: string | null;
}

export function useContradictionStatus(caseId: string | undefined) {
  return useQuery<ContradictionStatus, Error>({
    queryKey: ["contradiction-status", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const empty = { total: 0, assessed: 0, contradicted: 0, remaining: 0, last_built: null };
      if (!caseId) return empty;
      const { data, error } = await berserkr().rpc("fetch_contradiction_status", { p_case_id: caseId, p_build: BUILD });
      if (error) throw new Error(error.message);
      return (data as ContradictionStatus) ?? empty;
    },
  });
}

interface BatchResult { done: boolean; assessed: number; total: number; remaining: number; contradicted: number; }

/**
 * Muster the contradiction ledger. Resumable: the edge function processes one
 * batch per call, so we loop (reset on the first call) until done, refreshing
 * the ledger after each batch so it fills in progressively.
 */
export function useMusterContradictions(caseId: string | undefined) {
  const qc = useQueryClient();
  const [mustering, setMustering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ assessed: number; total: number; contradicted: number } | null>(null);

  const invoke = useCallback(async (reset: boolean): Promise<BatchResult> => {
    const sb = getSupabase();
    if (!sb) throw new Error("Supabase not configured");
    const { data, error: e } = await sb.functions.invoke("muster-contradictions", { body: { case_id: caseId, reset } });
    if (e) throw new Error(e.message);
    return data as BatchResult;
  }, [caseId]);

  const refresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["case-contradictions", caseId] });
    qc.invalidateQueries({ queryKey: ["contradiction-status", caseId] });
  }, [qc, caseId]);

  const muster = useCallback(async () => {
    if (!caseId || mustering) return;
    setError(null); setMustering(true); setProgress(null);
    try {
      let res = await invoke(true);
      setProgress({ assessed: res.assessed, total: res.total, contradicted: res.contradicted });
      refresh();
      let guard = 0;
      while (!res.done && guard < 60) {
        guard++;
        res = await invoke(false);
        setProgress({ assessed: res.total - res.remaining, total: res.total, contradicted: res.contradicted });
        refresh();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setMustering(false);
      refresh();
    }
  }, [caseId, mustering, invoke, refresh]);

  return { muster, mustering, error, progress };
}
